/* External */
import { memo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Assets */
import Icon from '~/assets/theme/svg/block-content.svg';

/* Components */
import { getJsonImage } from 'src/theme/images';
import FocusableButton from '../../components/FocusableButton';

/* Utils */
import useQuery from '../../hooks/useQuery';
import { gaServiceError } from '../../services/gaService';
import { useLoading } from '../../providers/LoadingProvider';
import { REPORT_GA, ERRORS } from '../../utils/constants';
import { redirectTo } from '../../utils/history';

/* New GTM to Errors */
import { useGtmLocalContext } from '../../providers/GtmLocalProvider';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../utils/googleTags';

/* Styles */
import './ErrorPage.scss';

const ERROR_PAGE_BUTTON_FOCUS_KEY = 'errorPageRedirectFocusKey';

function ErrorPage({ setFocus }) {
  const { error, errorCode, errorMessage } = useQuery();
  const { hideLoading } = useLoading();
  const location = useLocation();
  const { commonTaggingEvent } = useGtmLocalContext();

  /* New GTM for sending errors that disconnect the user */
  const errorsCommonTagEvent = useCallback(({ eventName, objErrors }) => {
    if (eventName && objErrors) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.ERRORS_DATA,
        subCategory: GTM_SUBCATEGORIES.ERRORS.ERROR_LOGOUT,
        eventName,
        data: {
          errorCode: `${objErrors?.errorCode}`,
          errorType: `${objErrors?.errorType}`,
          api: `${objErrors?.errorApi}`,
        },
      });
    }
  }, [commonTaggingEvent]);

  useEffect(() => {
    const errorApiReceived = location?.state?.error;
    if (errorApiReceived) {
      const objErrors = {
        errorType: errorApiReceived.errorMessage,
        errorCode: errorApiReceived.errorCodeWithDetails,
        errorApi: `${errorApiReceived?.config?.baseURL}${errorApiReceived?.config?.url}`,
      };
      if (objErrors.errorType && objErrors.errorCode && objErrors.errorApi) {
        errorsCommonTagEvent({
          eventName: GTM_EVENT_NAMES.PAGE_VIEW,
          objErrors,
        });
      }
    }
  }, [errorsCommonTagEvent, location?.state?.error]);

  const {
    gaEventCategory,
  } = ERRORS[error] || {};

  const handleEnterPress = useCallback(() => {
    redirectTo('/public/pin-login');
    if (gaEventCategory) {
      gaServiceError.sendEvent({
        category: gaEventCategory,
        action: REPORT_GA.GA_EVENT_ACTION_BUTTON_CLICK,
      });
    }
  }, [gaEventCategory]);

  useEffect(() => {
    hideLoading();
    setFocus(ERROR_PAGE_BUTTON_FOCUS_KEY);
  }, [hideLoading, setFocus]);

  useEffect(() => {
    if (gaEventCategory) {
      gaServiceError.sendEvent({
        category: gaEventCategory,
        action: REPORT_GA.GA_ERROR_EVENT_ACTION_ERROR_RECEIVED,
        label: location?.state?.error?.message,
      }, {
        errorDetail: location?.state?.error ? JSON.stringify(location?.state?.error) : undefined,
      });
    }
  }, [error, gaEventCategory, location?.state?.error]);

  return (
    <div className="dtv-html5-c-error-page">
      <img className="dtv-html5-c-error-page__logo" src={getJsonImage('block-content') || Icon} alt="dtv-logo" />
      <h1 className="dtv-html5-c-error-page__title">
        <FormattedMessage id="profile.error.auth.title" />
      </h1>
      <p className="dtv-html5-c-error-page__description">
        <FormattedMessage id={errorMessage} />
      </p>
      {errorCode && (
        <p className="dtv-html5-c-error-page__code">
          Código:
          {' '}
          {errorCode}
        </p>
      )}
      <FocusableButton
        className="dtv-html5-c-error-page__button"
        onEnterPress={handleEnterPress}
        focusKey={ERROR_PAGE_BUTTON_FOCUS_KEY}
        primary
      >
        <FormattedMessage id="profile.error.auth.buttonOk" />
      </FocusableButton>
    </div>
  );
}

ErrorPage.propTypes = {
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(ErrorPage)));
